// ─────────────────────────────────────────────────────────────────────
// THE TOURNAMENT LOG IS DATA, NOT PROSE (#519).
//
// A tournament's event log is PERSISTED with the tournament and read back by
// everyone who opens it — including, now that the catalog covers this surface, a
// reader who has chosen English. It used to store finished Norwegian sentences:
//
//     msg: `Turnering startet — fase ${phase}, runde ${round}`
//
// which means the language a tournament was CREATED in decides what language its
// own history is in, for ever. That is the same rule as «API and domain error
// codes are never translated» (CLAUDE.md, DOMAIN_RULES §4): a translated string
// must never be persisted as business data, because it cannot be corrected
// afterwards without rewriting history.
//
// So an entry stores WHAT HAPPENED and the values involved, and the sentence is
// decided when somebody looks at it.
//
// ── THE OLD ENTRIES STILL READ ───────────────────────────────────────
//
// Existing tournaments and archives are full of `msg` strings, and they are not
// migrated: there is nothing to migrate them TO. A sentence cannot be parsed
// back into the event that produced it, and guessing would put words in the
// record that nobody wrote. `renderLogEntry` therefore shows a stored `msg`
// verbatim, and that path is permanent rather than transitional.
//
// ── EVERY WRITER IS CONVERTED NOW ────────────────────────────────────
//
// The last prose writers went in the rest of #519: the engine's substitution
// and placeholder-fill entries (`packages/engine` americano.ts, padel24.ts,
// which write the code as a plain string because the engine cannot import
// this catalog), the parking / resume / archive entries in
// `state/useTournamentLifecycle.ts`, and the americano, preview and wizard
// views. Each Norwegian message is the exact sentence that writer used to
// store. `apps/web/tests/tournament-log-engine-codes.test.ts` pins that, checks
// every engine code has a message, and fails on any `{ time, msg }` entry
// written in apps/web/src or packages/engine/src.
// ─────────────────────────────────────────────────────────────────────

import type { MessageKey, Vars } from '../i18n';

/** The catalog keys that name a log event. */
type LogEventKey = Extract<MessageKey, `tournament.log.event.${string}`>;

/**
 * What can be logged.
 *
 * DERIVED FROM THE CATALOG, so the catalog is the single source of truth: a code
 * with no message is a type error at the call site, and a message with no code
 * is visible as an unused key. `nb` defines `MessageKey`, so adding an event
 * means adding it there first — which is the same direction every other string
 * in the app travels.
 */
export type LogCode = LogEventKey extends `tournament.log.event.${infer C}` ? C : never;

/** One entry in a tournament's event log. */
export interface TournamentLogEntry {
  /** When it happened, ISO-8601. */
  time: string;
  /** What happened. Absent on entries written before #519. */
  code?: LogCode;
  /**
   * The values the sentence needs — names, scores, phase numbers.
   *
   * STORED AS DATA, so they are the same in every language. A player's name is
   * not translated; a score is not localised on the way in. `translate`
   * formats numbers for the reader at render time.
   */
  vars?: Vars;
  /** Legacy prose, written before #519. Rendered verbatim, never parsed. */
  msg?: string;
}

/**
 * A log entry for `code`, stamped now.
 *
 * The `time` is an ISO instant rather than anything localised — it is compared
 * and sorted, and DOMAIN_RULES §4 governs anything stored.
 */
export function logEntry(code: LogCode, vars?: Vars): TournamentLogEntry {
  return vars === undefined
    ? { time: new Date().toISOString(), code }
    : { time: new Date().toISOString(), code, vars };
}

/**
 * The sentence for an entry, in the reader's language.
 *
 * `t` is the caller's translator, so this needs no hook and can be unit-tested
 * against either catalog.
 */
export function renderLogEntry(
  entry: TournamentLogEntry,
  t: (key: MessageKey, vars?: Vars) => string,
): string {
  if (entry.code) {
    return t(`tournament.log.event.${entry.code}` as MessageKey, entry.vars);
  }
  // Written before #519 was finished. Shown exactly as stored: it is somebody's
  // record of what happened.
  return entry.msg ?? '';
}
